import React, { useState } from 'react';
import { CONTACT_FIRST_FIELD_ID } from '../hooks/useScrollToContact';
import { Arrow, ctaClass, Eyebrow, FormAlert, inputClass, labelClass, Spinner } from './ui';

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
  general?: string;
}

const emptyForm: FormData = { name: '', email: '', subject: '', message: '' };

const infoRowClass = 'grid grid-cols-[100px_1fr] gap-3 border-b border-gray-200 py-3 lg:grid-cols-[130px_1fr]';
const infoLabelClass = 'pt-0.5 text-[13px] text-gray-500';
const infoLinkClass = 'font-semibold text-blue-700 hover:text-blue-600';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Betreff ist erforderlich';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Nachricht ist erforderlich';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Die Nachricht muss mindestens 10 Zeichen lang sein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/contact_form_smtp.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus('success');
        setFormData(emptyForm);
        setErrors({});
      } else {
        setSubmitStatus('error');
        setErrors({ general: result.message || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.' });
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrors({ general: 'Verbindungsfehler. Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Gemeinsame Props für Pflichtfelder inkl. Fehlerverknüpfung
  const fieldProps = (field: keyof FormData) => ({
    id: field === 'name' ? CONTACT_FIRST_FIELD_ID : `kontakt-${field}`,
    name: field,
    value: formData[field],
    onChange: handleInputChange,
    disabled: isSubmitting,
    'aria-invalid': !!errors[field],
    'aria-describedby': errors[field] ? `kontakt-${field}-error` : undefined,
  });

  const fieldError = (field: keyof FormData) =>
    errors[field] && (
      <p id={`kontakt-${field}-error`} className="mt-1 text-sm text-red-600">
        {errors[field]}
      </p>
    );

  return (
    <section id="kontakt" className="border-t border-gray-200 bg-gray-50 px-5 pb-11 pt-10 lg:border-0 lg:bg-transparent lg:p-0">
      <Eyebrow>Kontakt</Eyebrow>
      <h2 className="mb-3 text-[32px] font-extrabold leading-[1.05] tracking-[-0.02em] lg:text-[40px] lg:leading-none lg:tracking-[-0.025em]">
        Sprechen Sie uns an
      </h2>
      <p className="mb-6 text-base leading-normal text-gray-600">
        Haben Sie Fragen oder möchten Sie einen Gastflug buchen? Kontaktieren Sie uns!
      </p>

      <dl className="mb-7 border-t-2 border-gray-900 text-[15px] leading-normal lg:mb-0">
        <div className={infoRowClass}>
          <dt className={infoLabelClass}>Adresse</dt>
          <dd>
            Thamsbrücker Landstraße 28<br className="lg:hidden" />
            <span className="hidden lg:inline">, </span>99947 Bad Langensalza
          </dd>
        </div>
        <div className={infoRowClass}>
          <dt className={infoLabelClass}>Postanschrift</dt>
          <dd>
            Postfach 1241<br className="lg:hidden" />
            <span className="hidden lg:inline">, </span>99947 Bad Langensalza
          </dd>
        </div>
        <div className={infoRowClass}>
          <dt className={infoLabelClass}>Telefon</dt>
          <dd>
            <a href="tel:+4936038958503" className={infoLinkClass}>
              +49 3603 895 8503
            </a>
          </dd>
        </div>
        <div className={infoRowClass}>
          <dt className={infoLabelClass}>E-Mail</dt>
          <dd>
            <a href="mailto:info@flugsportverein.eu" className={infoLinkClass}>
              info@flugsportverein.eu
            </a>
          </dd>
        </div>
        <div className={infoRowClass}>
          <dt className={infoLabelClass}>Social Media</dt>
          <dd className="flex gap-4">
            <a href="https://www.instagram.com/flugsportverein_offiziell/" target="_blank" rel="noopener noreferrer" className={infoLinkClass}>
              Instagram
            </a>
            <a href="https://www.facebook.com/flugsportverein.otto.lilienthal" target="_blank" rel="noopener noreferrer" className={infoLinkClass}>
              Facebook
            </a>
          </dd>
        </div>
      </dl>

      <h3 className="mb-4 text-xl font-extrabold lg:sr-only">Schreiben Sie uns</h3>

      <form onSubmit={handleSubmit} noValidate className="grid gap-3.5 lg:mt-7 lg:grid-cols-2">
        {submitStatus === 'success' && (
          <FormAlert type="success" className="lg:col-span-2">
            Vielen Dank! Ihre Nachricht wurde erfolgreich gesendet. Wir werden uns so schnell wie möglich bei Ihnen melden.
          </FormAlert>
        )}

        {errors.general && (
          <FormAlert type="error" className="lg:col-span-2">
            {errors.general}
          </FormAlert>
        )}

        <div>
          <label htmlFor={CONTACT_FIRST_FIELD_ID} className={labelClass}>
            Name *
          </label>
          <input
            type="text"
            autoComplete="name"
            placeholder="Ihr vollständiger Name"
            className={`${inputClass(!!errors.name)} h-12 lg:h-[46px]`}
            {...fieldProps('name')}
          />
          {fieldError('name')}
        </div>

        <div>
          <label htmlFor="kontakt-email" className={labelClass}>
            E-Mail *
          </label>
          <input
            type="email"
            autoComplete="email"
            placeholder="ihre.email@beispiel.de"
            className={`${inputClass(!!errors.email)} h-12 lg:h-[46px]`}
            {...fieldProps('email')}
          />
          {fieldError('email')}
        </div>

        <div className="lg:col-span-2">
          <label htmlFor="kontakt-subject" className={labelClass}>
            Betreff *
          </label>
          <input
            type="text"
            placeholder="Worum geht es in Ihrer Nachricht?"
            className={`${inputClass(!!errors.subject)} h-12 lg:h-[46px]`}
            {...fieldProps('subject')}
          />
          {fieldError('subject')}
        </div>

        <div className="lg:col-span-2">
          <label htmlFor="kontakt-message" className={labelClass}>
            Nachricht *
          </label>
          <textarea
            rows={4}
            placeholder="Teilen Sie uns mit, wie wir Ihnen helfen können..."
            className={`${inputClass(!!errors.message)} resize-y py-3`}
            {...fieldProps('message')}
          />
          {fieldError('message')}
          <p className="mt-1 text-xs text-gray-500">Mindestens 10 Zeichen erforderlich</p>
        </div>

        <div className="flex flex-col gap-3.5 lg:col-span-2 lg:flex-row lg:items-center lg:justify-between lg:gap-5">
          <button type="submit" disabled={isSubmitting} className={`${ctaClass} text-base lg:text-[15px]`}>
            {isSubmitting ? (
              <span className="inline-flex items-center gap-3">
                <Spinner /> Wird gesendet...
              </span>
            ) : (
              <>
                Nachricht senden <Arrow />
              </>
            )}
          </button>
          <p className="text-xs leading-normal text-gray-500 lg:max-w-[34ch] lg:leading-snug">
            * Pflichtfelder. Ihre Daten werden vertraulich behandelt und nicht an Dritte weitergegeben.
          </p>
        </div>
      </form>
    </section>
  );
};

export default Contact;
